import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';

const PLY_FALLBACK_MODEL_COLOR = 0xffffff;
const STL_MODEL_COLOR = 0x807b71;
const TOP_BOTTOM_EPSILON = 0.001;
const CAMERA_STATE_EPSILON = 0.001;
const WORLD_UP = new THREE.Vector3(0, 1, 0);

const CAMERA_DIRECTIONS = {
    behind: new THREE.Vector3(0, 0, -1),
    bottom: new THREE.Vector3(0, -1, TOP_BOTTOM_EPSILON),
    front: new THREE.Vector3(0, 0, 1),
    left: new THREE.Vector3(-1, 0, 0),
    right: new THREE.Vector3(1, 0, 0),
    top: new THREE.Vector3(0, 1, TOP_BOTTOM_EPSILON),
};

export function getViewportSize(mount) {
    return {
        width: Math.max(mount?.clientWidth || 1, 1),
        height: Math.max(mount?.clientHeight || 1, 1),
    };
}

export function addStudioLights(scene) {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.65);
    scene.add(ambientLight);

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xd6e2ff, 1.05);
    scene.add(hemiLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.95);
    keyLight.position.set(1.8, 1.3, 2.4);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-1.6, 0.8, 1.2);
    scene.add(fillLight);
}

export function createSceneEnvironment(renderer) {
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    const roomEnvironment = new RoomEnvironment(renderer);
    const sceneEnvironment = pmremGenerator.fromScene(roomEnvironment, 0.04);

    return {
        pmremGenerator,
        roomEnvironment,
        sceneEnvironment,
    };
}

export function disposeSceneEnvironment(environmentResources) {
    if (!environmentResources) {
        return;
    }

    environmentResources.sceneEnvironment?.dispose();
    environmentResources.roomEnvironment?.dispose();
    environmentResources.pmremGenerator?.dispose();
}

function getVisibleObjects(renderHandles) {
    return renderHandles
        .map((renderHandle) => renderHandle?.object3D)
        .filter((object3D) => object3D && object3D.visible);
}

function getBoundsForObjects(objects) {
    if (objects.length === 0) {
        return null;
    }

    const box = new THREE.Box3();
    objects.forEach((object3D) => {
        box.expandByObject(object3D);
    });

    const sphere = box.getBoundingSphere(new THREE.Sphere());

    return {
        box,
        sphere,
    };
}

function getFitDistance(camera, radius) {
    const fitHeightDistance = radius / Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5));
    const fitWidthDistance = fitHeightDistance / camera.aspect;

    return Math.max(fitHeightDistance, fitWidthDistance) * 1.18;
}

function getUpVectorForPreset() {
    return WORLD_UP.clone();
}

function buildCameraState(camera, target, direction, distance, preset = 'front', upVector = null) {
    const normalizedDirection = direction.clone().normalize();
    const safeDistance = Math.max(distance, 0.01);

    return {
        far: Math.max(safeDistance * 25, 100),
        minDistance: Math.max(safeDistance * 0.15, 0.05),
        maxDistance: safeDistance * 8,
        near: Math.max(safeDistance / 100, 0.01),
        position: target.clone().add(normalizedDirection.multiplyScalar(safeDistance)),
        target: target.clone(),
        up: (upVector || getUpVectorForPreset(preset)).clone(),
        zoom: camera.zoom,
    };
}

export function applyCameraState(camera, controls, cameraState) {
    if (!camera || !controls || !cameraState) {
        return false;
    }

    const targetCamera = camera;
    const targetControls = controls;
    const wasDampingEnabled = targetControls.enableDamping;

    // Flush any in-flight OrbitControls inertia before applying a programmatic
    // camera state, otherwise the previous drag's deltas can contaminate the
    // newly-applied reset/preset state for one extra frame.
    targetControls.enableDamping = false;
    targetControls.update();

    targetCamera.up.copy(cameraState.up);
    targetCamera.position.copy(cameraState.position);
    targetCamera.near = cameraState.near;
    targetCamera.far = cameraState.far;
    targetCamera.zoom = cameraState.zoom;
    targetCamera.lookAt(cameraState.target);
    targetCamera.updateProjectionMatrix();

    targetControls.target.copy(cameraState.target);
    targetControls.minDistance = cameraState.minDistance;
    targetControls.maxDistance = cameraState.maxDistance;
    targetControls.enablePan = true;
    targetControls.update();
    targetControls.enableDamping = wasDampingEnabled;

    return true;
}

export function syncControlsResetState(camera, controls, cameraState) {
    if (!camera || !controls || !cameraState) {
        return false;
    }

    const targetControls = controls;

    targetControls.target0.copy(cameraState.target);
    targetControls.position0.copy(cameraState.position);
    targetControls.zoom0 = cameraState.zoom;

    return true;
}

export function getResetViewCameraState(camera, renderHandles) {
    const visibleObjects = getVisibleObjects(renderHandles);
    const bounds = getBoundsForObjects(visibleObjects);

    if (!camera || !bounds) {
        return null;
    }

    const center = bounds.sphere.center.clone();
    const radius = Math.max(bounds.sphere.radius, 0.01);
    const distance = getFitDistance(camera, radius);

    return buildCameraState(
        camera,
        center,
        CAMERA_DIRECTIONS.front,
        distance,
        'front',
    );
}

export function doesCameraMatchState(camera, controls, cameraState) {
    if (!camera || !controls || !cameraState) {
        return false;
    }

    const targetMatches = controls.target.distanceToSquared(cameraState.target) <= CAMERA_STATE_EPSILON;
    const positionMatches = camera.position.distanceToSquared(cameraState.position) <= CAMERA_STATE_EPSILON;
    const upMatches = camera.up.distanceToSquared(cameraState.up) <= CAMERA_STATE_EPSILON;
    const zoomMatches = Math.abs(camera.zoom - cameraState.zoom) <= CAMERA_STATE_EPSILON;

    return targetMatches && positionMatches && upMatches && zoomMatches;
}

export function applyCameraPreset(camera, controls, renderHandles, preset) {
    if (preset === 'resetView') {
        return applyCameraState(
            camera,
            controls,
            getResetViewCameraState(camera, renderHandles),
        );
    }

    if (!camera || !controls) {
        return false;
    }

    const distance = Math.max(camera.position.distanceTo(controls.target), 0.01);
    const direction = CAMERA_DIRECTIONS[preset] || CAMERA_DIRECTIONS.front;
    const cameraState = buildCameraState(
        camera,
        controls.target,
        direction,
        distance,
        preset,
    );

    return applyCameraState(camera, controls, cameraState);
}

function rotateAndCenterGeometry(geometry, preservePlacement = false) {
    geometry.rotateX(-Math.PI / 2);
    geometry.computeBoundingSphere();

    if (!preservePlacement) {
        const center = geometry.boundingSphere?.center || new THREE.Vector3();
        geometry.translate(-center.x, -center.y, -center.z);
    }

    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
}

function disposeObjectTree(object3D) {
    object3D.traverse((child) => {
        if (child.geometry) {
            child.geometry.dispose();
        }

        if (child.material) {
            if (Array.isArray(child.material)) {
                child.material.forEach((material) => material.dispose());
            } else {
                child.material.dispose();
            }
        }
    });
}

function createHandle(object3D, applyAppearance, disposeResources, capabilities = {}) {
    const targetObject3D = object3D;

    return {
        applyAppearance,
        capabilities,
        dispose() {
            disposeObjectTree(targetObject3D);
            disposeResources();
        },
        object3D: targetObject3D,
        setVisible(isVisible) {
            targetObject3D.visible = isVisible;
        },
    };
}

function buildInvalidatedResourceRefs(type, url) {
    return url ? [{ type, url }] : [];
}

function buildStlMesh(geometry) {
    const material = new THREE.MeshPhysicalMaterial({
        clearcoat: 0.03,
        clearcoatRoughness: 0.75,
        color: STL_MODEL_COLOR,
        envMapIntensity: 0.12,
        metalness: 0,
        roughness: 0.62,
        side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    return {
        material,
        mesh,
    };
}

function applyStlAppearance(material) {
    const targetMaterial = material;

    targetMaterial.color.setHex(STL_MODEL_COLOR);
    targetMaterial.needsUpdate = true;
}

export function buildStlRenderable(model, asset, appearance) {
    const loader = new STLLoader();
    let geometry;

    try {
        geometry = loader.parse(asset.geometryBuffer);
    } catch (error) {
        error.invalidatedResources = buildInvalidatedResourceRefs('geometry-stl', model.geometryUrl);
        throw error;
    }

    const positionAttribute = geometry.getAttribute('position');

    if (!positionAttribute || positionAttribute.count === 0) {
        geometry.dispose();
        return null;
    }

    rotateAndCenterGeometry(geometry, model.preservePlacement);

    if (!geometry.getAttribute('normal')) {
        geometry.computeVertexNormals();
    }

    const { material, mesh } = buildStlMesh(geometry);
    const renderHandle = createHandle(
        mesh,
        () => applyStlAppearance(material),
        () => {},
        {
            supportsColor: true,
            supportsTexture: false,
        },
    );

    renderHandle.applyAppearance(appearance);

    return renderHandle;
}

function hasUsableVertexColors(colorAttribute) {
    if (!colorAttribute || colorAttribute.count === 0) {
        return false;
    }

    const sampleCount = Math.min(colorAttribute.count, 500);
    const step = Math.max(1, Math.floor(colorAttribute.count / sampleCount));
    let totalLuminance = 0;
    let brightestSample = 0;
    let sampled = 0;

    for (let index = 0; index < colorAttribute.count; index += step) {
        const r = colorAttribute.getX(index);
        const g = colorAttribute.getY(index);
        const b = colorAttribute.getZ(index);
        const luminance = (0.2126 * r) + (0.7152 * g) + (0.0722 * b);

        totalLuminance += luminance;
        brightestSample = Math.max(brightestSample, luminance);
        sampled += 1;
    }

    const averageLuminance = sampled > 0 ? totalLuminance / sampled : 0;

    return brightestSample > 0.05 && averageLuminance > 0.015;
}

function createPointSprite() {
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    context.fillRect(0, 0, size, size);

    const pointSpriteTexture = new THREE.CanvasTexture(canvas);
    pointSpriteTexture.colorSpace = THREE.SRGBColorSpace;

    return pointSpriteTexture;
}

function loadTextureFromBlob(textureBlob) {
    return new Promise((resolve, reject) => {
        const textureLoader = new THREE.TextureLoader();
        const objectUrl = URL.createObjectURL(textureBlob);

        textureLoader.load(
            objectUrl,
            (texture) => {
                const targetTexture = texture;

                URL.revokeObjectURL(objectUrl);
                targetTexture.colorSpace = THREE.SRGBColorSpace;
                targetTexture.flipY = true;
                targetTexture.needsUpdate = true;
                resolve(targetTexture);
            },
            undefined,
            (error) => {
                URL.revokeObjectURL(objectUrl);
                reject(error);
            },
        );
    });
}

function buildPlyMesh(geometry) {
    const material = new THREE.MeshPhysicalMaterial({
        clearcoat: 0.2,
        clearcoatRoughness: 0.08,
        color: new THREE.Color(PLY_FALLBACK_MODEL_COLOR),
        envMapIntensity: 0,
        ior: 1.62,
        metalness: 0,
        roughness: 0.45,
        side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    return {
        material,
        mesh,
    };
}

function buildPlyPointCloud(geometry) {
    const pointSpriteTexture = createPointSprite();
    const radius = Math.max(geometry.boundingSphere?.radius || 1, 1);
    const material = new THREE.PointsMaterial({
        alphaTest: 0.35,
        color: new THREE.Color(PLY_FALLBACK_MODEL_COLOR),
        depthWrite: false,
        map: pointSpriteTexture,
        opacity: 0.95,
        size: Math.max(radius * 0.006, 0.035),
        sizeAttenuation: true,
        transparent: true,
    });

    return {
        material,
        pointCloud: new THREE.Points(geometry, material),
        pointSpriteTexture,
    };
}

function applyPlyMeshAppearance(material, baseTexture, useVertexColors, appearance) {
    const targetMaterial = material;
    const shouldUseTexture = appearance.showTexture && Boolean(baseTexture);
    const shouldUseVertexColors = !shouldUseTexture && useVertexColors;

    targetMaterial.map = shouldUseTexture ? baseTexture : null;
    targetMaterial.vertexColors = shouldUseVertexColors;
    targetMaterial.color.setHex(PLY_FALLBACK_MODEL_COLOR);
    targetMaterial.roughness = shouldUseVertexColors ? 0.58 : 0.45;
    targetMaterial.needsUpdate = true;
}

function applyPlyPointCloudAppearance(material, useVertexColors) {
    const targetMaterial = material;

    targetMaterial.vertexColors = useVertexColors;
    targetMaterial.color.setHex(PLY_FALLBACK_MODEL_COLOR);
    targetMaterial.needsUpdate = true;
}

export async function buildPlyRenderable(model, asset, appearance) {
    const loader = new PLYLoader();
    let geometry;

    try {
        geometry = loader.parse(asset.geometryBuffer);
    } catch (error) {
        error.invalidatedResources = buildInvalidatedResourceRefs('geometry-ply', model.geometryUrl);
        throw error;
    }

    const positionAttribute = geometry.getAttribute('position');

    if (!positionAttribute || positionAttribute.count === 0) {
        geometry.dispose();
        return null;
    }

    rotateAndCenterGeometry(geometry, model.preservePlacement);

    const useVertexColors = hasUsableVertexColors(geometry.getAttribute('color'));
    const isPointCloud = geometry.index === null && !geometry.getAttribute('normal');
    // If the image blob downloads but Three cannot decode it, keep rendering the PLY
    // without texture instead of failing the whole model build.
    const invalidatedResources = [];
    const baseTexture = asset.textureBlob
        ? await loadTextureFromBlob(asset.textureBlob).catch(() => {
            invalidatedResources.push(...buildInvalidatedResourceRefs('texture', model.textureUrl));
            return null;
        })
        : null;

    if (isPointCloud) {
        const { material, pointCloud, pointSpriteTexture } = buildPlyPointCloud(geometry);
        const renderHandle = createHandle(
            pointCloud,
            () => applyPlyPointCloudAppearance(material, useVertexColors),
            () => {
                pointSpriteTexture.dispose();
                baseTexture?.dispose();
            },
            {
                supportsColor: useVertexColors,
                supportsTexture: false,
            },
        );

        if (invalidatedResources.length > 0) {
            renderHandle.invalidatedResources = invalidatedResources;
        }

        renderHandle.applyAppearance(appearance);

        return renderHandle;
    }

    if (!geometry.getAttribute('normal')) {
        geometry.computeVertexNormals();
    }

    const { material, mesh } = buildPlyMesh(geometry);
    const renderHandle = createHandle(
        mesh,
        (nextAppearance) => applyPlyMeshAppearance(material, baseTexture, useVertexColors, nextAppearance),
        () => {
            baseTexture?.dispose();
        },
        {
            supportsColor: useVertexColors,
            supportsTexture: Boolean(baseTexture),
        },
    );

    if (invalidatedResources.length > 0) {
        renderHandle.invalidatedResources = invalidatedResources;
    }

    renderHandle.applyAppearance(appearance);

    return renderHandle;
}

export function buildRenderableHandle(model, asset, appearance) {
    return Promise.resolve().then(() => {
        if (model.format === 'ply') {
            return buildPlyRenderable(model, asset, appearance);
        }

        return buildStlRenderable(model, asset, appearance);
    });
}
